'use client';

import { useTranslations } from 'next-intl';
import {
  Checkbox,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { TRACKER_BULK_REQUEST_LIMIT } from '@terra/shared-contracts-tracker';
import { Database01Icon, Message01Icon } from '@terra/ui-icons';

export const BULK_HTTP_REQUEST_LIMIT = TRACKER_BULK_REQUEST_LIMIT;
type HttpHistoryBulkActionsBarProps = {
  selectedCount: number;
  availableCount: number;
  allSelected: boolean;
  hasPartialSelection: boolean;
  isSendToTrackingLoading: boolean;
  isSendToCopilotLoading: boolean;
  hideSendToTracking?: boolean;
  onSelectAll: (selected: boolean) => void;
  onSendToTracking: () => void;
  onSendToCopilot: () => void;
};

export function HttpHistoryBulkActionsBar({
  selectedCount,
  availableCount,
  allSelected,
  hasPartialSelection,
  isSendToTrackingLoading,
  isSendToCopilotLoading,
  hideSendToTracking = false,
  onSelectAll,
  onSendToTracking,
  onSendToCopilot,
}: HttpHistoryBulkActionsBarProps) {
  const t = useTranslations('httpHistory.bulkActions');
  const tCommon = useTranslations('common');
  const hasSelection = selectedCount > 0;
  const isOverLimit = selectedCount > BULK_HTTP_REQUEST_LIMIT;
  const isAnyActionLoading = isSendToTrackingLoading || isSendToCopilotLoading;
  const isActionDisabled = !hasSelection || isAnyActionLoading || isOverLimit;
  const checkboxState: boolean | 'indeterminate' = allSelected
    ? true
    : hasPartialSelection
      ? 'indeterminate'
      : false;
  const actionTooltip = isOverLimit
    ? t('limitTooltip', { count: BULK_HTTP_REQUEST_LIMIT })
    : undefined;

  return (
    <Box
      w="full"
      px="1"
      bg="bg.canvas"
      borderBottomWidth="1px"
      borderColor="border.subtle"
    >
      <HStack
        gap="1"
        alignItems="center"
        justifyContent="space-between"
        pl="3"
        h="31px"
      >
        <Checkbox
          size="sm"
          checked={checkboxState}
          disabled={availableCount === 0 || isAnyActionLoading}
          onCheckedChange={(details) => onSelectAll(details.checked === true)}
          flexShrink={0}
        >
          <Text fontSize="xs" color="fg.default" whiteSpace="nowrap">
            {t('selectLoaded', { count: availableCount })}
          </Text>
        </Checkbox>

        <HStack
          gap="1"
          alignItems="center"
          justifyContent="flex-end"
          flexShrink={0}
        >
          {hasSelection && (
            <Text fontSize="xs" color="fg.muted" mr="1" flexShrink={0}>
              {tCommon('selectedCount', { count: selectedCount })}
            </Text>
          )}

          {!hideSendToTracking ? (
            <Tooltip title={actionTooltip ?? t('sendToTrackingTooltip')}>
              <Box display="inline-flex" flexShrink={0}>
                <IconButton
                  aria-label={t('sendToTracking')}
                  data-tour-id="send-to-tracking"
                  size="xs"
                  variant="ghost"
                  disabled={isActionDisabled}
                  onClick={onSendToTracking}
                  flexShrink={0}
                >
                  {isSendToTrackingLoading ? (
                    <Spinner size="xs" />
                  ) : (
                    <Database01Icon size={12} />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          ) : null}

          <Tooltip title={actionTooltip ?? t('sendToCopilotTooltip')}>
            <Box display="inline-flex" flexShrink={0}>
              <IconButton
                aria-label={t('sendToCopilot')}
                size="xs"
                variant="ghost"
                disabled={isActionDisabled}
                onClick={onSendToCopilot}
                flexShrink={0}
              >
                {isSendToCopilotLoading ? (
                  <Spinner size="xs" />
                ) : (
                  <Message01Icon size={12} />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
}
